import type React from "react"
import type { Document } from "@/lib/api"

export interface DocumentRendererProps {
  document: Document
  sourceViewActive: boolean
}

export type DocumentRendererCapabilities = {
  /** Renderer owns immutable/non-text content, so the shell must not offer Save. */
  saveable?: boolean
  /** Whether a document-level Open in Cursor action is meaningful. */
  openInCursor?: boolean
  /** Download behavior exposed by the shared document overflow menu. */
  download?: "pdf" | "original" | false
}

export type DocumentRendererRegistrationOptions = DocumentRendererCapabilities & {
  /** File extensions that select this renderer before document metadata finishes loading. */
  fileExtensions?: readonly string[]
}

type DocumentRendererRegistration = {
  component: React.ComponentType<DocumentRendererProps>
  capabilities: DocumentRendererCapabilities
  fileExtensions: readonly string[]
}

const registry = new Map<string, DocumentRendererRegistration>()

export function registerRenderer(
  kind: string,
  component: React.ComponentType<DocumentRendererProps>,
  options: DocumentRendererRegistrationOptions = {},
) {
  const { fileExtensions = [], ...capabilities } = options
  registry.set(kind, {
    component,
    capabilities,
    fileExtensions: fileExtensions.map((extension) => extension.toLowerCase()),
  })
}

export function getRenderer(kind: string) {
  return registry.get(kind)?.component ?? null
}

export function getRendererCapabilities(kind: string): Required<DocumentRendererCapabilities> {
  const capabilities = registry.get(kind)?.capabilities
  return {
    saveable: capabilities?.saveable ?? true,
    openInCursor: capabilities?.openInCursor ?? true,
    download: capabilities?.download ?? "pdf",
  }
}

export function resolveDocumentRendererKind(options: {
  kind?: string | null
  type?: string | null
  filePath?: string | null
}): string {
  const kind = String(options.kind || "").trim()
  if (kind && registry.has(kind)) return kind
  const type = String(options.type || "").trim()
  if (type && registry.has(type)) return type
  const filePath = String(options.filePath || "").toLowerCase()
  if (filePath) {
    for (const [registeredKind, registration] of registry) {
      if (registration.fileExtensions.some((extension) => filePath.endsWith(extension))) {
        return registeredKind
      }
    }
  }
  return kind || type || "note"
}
